import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Money, 
  EmojiEvents,
  Person,
  Image,
  AccountTree,
  Assessment,
  Calculate,
  CalendarMonth,
  School as SchoolIcon,
  NavigateNext,
  ExpandMore,
  Book,
  Bookmark,
  Share,
  Send,
  LocationOn,
  Work,
  Description,
  CheckCircle,
  Star,
  Timer
} from '@mui/icons-material';
import { programService, programDataMapper } from '../services/programService';
import ShareButtonAdvanced from '../components/common/ShareButtonAdvanced';
import BookmarkButton from '../components/common/BookmarkButton';

const ProgramDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleApplyNow = () => {
    navigate('/services');
  };

  const handleViewUniversity = () => {
    if (program?.university?.sid) {
      navigate(`/universities/${program.university.sid}`);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    programService.getProgramDetail(id)
      .then(res => {
        const data = res.data?.data || res.data;
        const mappedProgram = programDataMapper.mapProgramFromApi(data);
        setProgram(mappedProgram);
      })
      .catch(err => {
        setError(err.message || '加载失败');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>正在加载专业详情...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Typography>无法加载专业信息，请稍后重试。</Typography>
        </Container>
      </Box>
    );
  }

  if (!program) {
    return null;
  }

  // 统计卡片组件
  const StatCard = ({ icon: Icon, title, value, color = "primary", subtitle }) => (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            bgcolor: `${color}.main`,
            color: 'white',
            borderRadius: '50%',
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
          {value || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', py: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* 面包屑导航 */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 3 }}
          aria-label="breadcrumb"
        >
          <Link color="inherit" href="/" underline="hover">
            首页
          </Link>
          <Link color="inherit" href="/programs" underline="hover">
            专业搜索
          </Link>
          <Typography color="text.primary">{program.name || '专业详情'}</Typography>
        </Breadcrumbs>

        {/* 专业基本信息头部 */}
        <Card sx={{ mb: 4, position: 'relative', overflow: 'visible' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 120,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px 12px 0 0',
              zIndex: 0
            }}
          />
          <CardContent sx={{ pt: 8, pb: 4, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
                  <Chip 
                    label={program.level} 
                    color="primary" 
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={program.mode || '课程型'} 
                    variant="outlined"
                    sx={{ 
                      fontWeight: 600,
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.5)',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  />
                  {program.featured && (
                    <Chip 
                      icon={<Star />} 
                      label="推荐专业" 
                      color="warning"
                      variant="filled"
                    />
                  )}
                </Stack>
                <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom sx={{ position: 'relative', zIndex: 2, lineHeight: 1.3 }}>
                  {program.name || '专业名称加载中...'}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 2, lineHeight: 1.4 }}>
                  {program.nameEn || 'English Name Loading...'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7, position: 'relative', zIndex: 2 }}>
                  {program.description}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 2, mt: 2 }}>
                  <Avatar 
                    src={program.university.logo} 
                    sx={{ width: 40, height: 40 }}
                  >
                    <SchoolIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                      {program.university.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {program.faculty} {program.location && `• ${program.location}`}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                                     <Button 
                     variant="contained" 
                     size="large" 
                     fullWidth
                     startIcon={<Send />}
                     onClick={handleApplyNow}
                     sx={{ py: 1.5, fontWeight: 600 }}
                   >
                     立即申请
                   </Button>
                  <Stack direction="row" spacing={1}>
                    <BookmarkButton
                      itemId={program.id}
                      itemType="program"
                      title={program.name}
                      description={program.description}
                      image={program.image}
                      variant="outlined"
                      fullWidth
                      onBookmarkChange={(action, data) => {
                        console.log('收藏状态变化:', action, data);
                        // 这里可以添加收藏统计逻辑
                      }}
                    />
                    <ShareButtonAdvanced
                      title={program.name || '专业详情'}
                      url={window.location.href}
                      description={program.description}
                      image={program.image}
                      hashtags={['留学', '教育', program.level, program.university.name]}
                      platforms={['wechat', 'weibo', 'qq', 'qzone', 'xiaohongshu', 'douyin', 'copy']}
                      showQRCode={true}
                      showPrint={true}
                      showDownload={false}
                      variant="outlined"
                      fullWidth
                      onShare={(platform, data) => {
                        console.log('分享到:', platform, data);
                        // 这里可以添加分享统计逻辑
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 主要内容区域 */}
        <Grid container spacing={4}>
          {/* 左侧内容 */}
          <Grid item xs={12} lg={8}>
            {/* 标签页导航 */}
            <Card sx={{ mb: 4 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="课程信息" icon={<Book />} iconPosition="start" />
                <Tab label="入学要求" icon={<Assessment />} iconPosition="start" />
                <Tab label="费用详情" icon={<Money />} iconPosition="start" />
                <Tab label="申请流程" icon={<Work />} iconPosition="start" />
              </Tabs>

              {/* 课程信息标签页 */}
              {activeTab === 0 && (
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        必修课程
                      </Typography>
                                             {program.curriculum && program.curriculum.length > 0 ? (
                         <List dense>
                           {program.curriculum.map((course, index) => (
                             <ListItem key={index} sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText 
                                 primary={course}
                                 primaryTypographyProps={{ variant: 'body2' }}
                               />
                             </ListItem>
                           ))}
                         </List>
                       ) : (
                         <Typography variant="body2" color="text.secondary">
                           暂无必修课信息
                         </Typography>
                       )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        选修课程
                      </Typography>
                                             {program.electiveCourses && program.electiveCourses.length > 0 ? (
                         <List dense>
                           {program.electiveCourses.map((course, index) => (
                             <ListItem key={index} sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <Star color="secondary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText 
                                 primary={course}
                                 primaryTypographyProps={{ variant: 'body2' }}
                               />
                             </ListItem>
                           ))}
                         </List>
                       ) : (
                         <Typography variant="body2" color="text.secondary">
                           暂无选修课信息
                         </Typography>
                       )}
                    </Grid>
                  </Grid>
                </CardContent>
              )}

              {/* 入学要求标签页 */}
              {activeTab === 1 && (
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="h6" fontWeight={600}>
                            学术要求
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" color="text.secondary">
                            {program.requirements.academic}
                          </Typography>
                          {program.requirements.gpa !== '-' && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                GPA要求：{program.requirements.gpa}
                              </Typography>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="h6" fontWeight={600}>
                            语言要求
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" color="text.secondary">
                            {program.requirements.language}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="h6" fontWeight={600}>
                            申请材料
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {program.requirements.documents.map((doc, index) => (
                              <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Description color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={doc} />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </CardContent>
              )}

              {/* 费用详情标签页 */}
              {activeTab === 2 && (
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            费用详情
                          </Typography>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography>学费</Typography>
                              <Typography fontWeight={600}>
                                {program.tuitionFee !== '-' 
                                  ? `RM ${program.tuitionFee}` 
                                  : '-'
                                }
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography>杂费</Typography>
                              <Typography fontWeight={600}>
                                {program.miscellaneousFee !== '-' 
                                  ? `RM ${program.miscellaneousFee}` 
                                  : '-'
                                }
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            总费用
                          </Typography>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography>总费用</Typography>
                              <Typography fontWeight={600} color="primary.main" variant="h6">
                                {program.totalFee > 0 
                                  ? `RM ${program.totalFee.toFixed(2)}` 
                                  : '-'
                                }
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                * 总费用 = 学费 + 杂费
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            奖学金信息
                          </Typography>
                          {program.scholarship ? (
                            <Stack spacing={2}>
                              <Typography variant="body2" color="success.main" fontWeight={600}>
                                ✓ 提供奖学金
                              </Typography>
                              {program.scholarshipAmount > 0 && (
                                <Typography variant="body2">
                                  奖学金金额：RM {program.scholarshipAmount}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              暂无奖学金信息
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              )}

              {/* 申请流程标签页 */}
              {activeTab === 3 && (
                <CardContent sx={{ p: 4 }}>
                  {/* <Typography variant="h6" fontWeight={600} gutterBottom>
                    申请流程
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    了解完整的申请流程和要求
                  </Typography> */}
                                     <Button 
                     variant="contained" 
                     size="large"
                     startIcon={<Send />}
                     onClick={handleApplyNow}
                     sx={{ fontWeight: 600 }}
                   >
                     开始申请流程
                   </Button>
                </CardContent>
              )}
            </Card>
          </Grid>

          {/* 右侧统计信息 */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* 关键信息卡片 */}
              <Card>
                <CardHeader
                  title="关键信息"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Timer}
                        title="学制"
                        value={program.duration}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Calculate}
                        title="学分"
                        value={program.credits}
                        color="secondary"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={CalendarMonth}
                        title="入学时间"
                        value={program.intakeMonths?.join(', ') || '-'}
                        color="success"
                        subtitle="月份"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Person}
                        title="面试要求"
                        value={program.interview}
                        color="warning"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* 大学信息卡片 */}
              <Card>
                <CardHeader
                  title="大学信息"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center" spacing={2}>
                      <Avatar 
                        src={program.university.logo} 
                        sx={{ width: 48, height: 48, mr: 2 }}
                      >
                        <SchoolIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {program.university.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {program.university.nameEn}
                        </Typography>
                      </Box>
                    </Box>
                    {program.university.ranking !== '-' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          世界排名
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary">
                          #{program.university.ranking}
                        </Typography>
                      </Box>
                    )}
                    <Button 
                      variant="outlined" 
                      fullWidth
                      startIcon={<LocationOn />}
                      onClick={handleViewUniversity}
                    >
                      查看大学详情
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* 特色标签 */}
              <Card>
                <CardHeader
                  title="专业特色"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    {program.portfolio && (
                      <Chip 
                        icon={<Image />} 
                        label="需要作品集" 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                    {program.crossMajor && (
                      <Chip 
                        icon={<AccountTree />} 
                        label="可跨专业申请" 
                        color="success" 
                        variant="outlined"
                      />
                    )}
                    {program.featured && (
                      <Chip 
                        icon={<Star />} 
                        label="推荐专业" 
                        color="warning" 
                        variant="outlined"
                      />
                    )}
                    {program.scholarship && (
                      <Chip 
                        icon={<EmojiEvents />} 
                        label="提供奖学金" 
                        color="info" 
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProgramDetail; 